import React, {useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {InputText} from "primereact/inputtext";
import {ScheduledTaskDTO} from "api/apiModels";
import {IPair} from "models/dataset-models";
import {InputChangeEvent} from "components/pages/scheduled-task-airflow/components/InputFields";

interface IConstantMapTableProps {
    property: keyof ScheduledTaskDTO;
    constants: Record<string, string>
    onChange: (e: InputChangeEvent<Record<string, string>>) => void;
}

interface IConstRow {
    key: string,
    value: string,
    newKey: string,
    newValue: string,
    editMode: boolean,
}

export default function TaskConfigConstantMapTable(props: IConstantMapTableProps) {
    const [constArr, setConstArr] = useState(mapToList(props.constants))

    const propagateChangesStateUp = (arr: IPair[]) => {
        const c: Record<string, string> = {}

        arr.forEach((e) => {
            c[e.key] = e.value;
        })

        props.onChange({
            property: props.property,
            value: c
        });
    }

    const confirmWrapper = (event: any, confirmHandler: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you really want to delete this constant?',
            icon: 'pi pi-exclamation-triangle',
            accept: confirmHandler,
        });
    }

    const handleConstEdit = (row: IConstRow) => {
        const idx = constArr.indexOf(row)
        constArr[idx].editMode = true
        constArr[idx].newValue = constArr[idx].value
        constArr[idx].newKey = constArr[idx].key
        setConstArr([...constArr])
    }

    const handleConstEditCancel = (row: IConstRow) => {
        const idx = constArr.indexOf(row)
        constArr[idx].editMode = false
        constArr[idx].newValue = constArr[idx].value
        constArr[idx].newKey = constArr[idx].key
        setConstArr([...constArr])
    }

    const handleConstEditConfirm = (row: IConstRow) => {
        const idx = constArr.indexOf(row)
        constArr[idx].editMode = false
        constArr[idx].value = constArr[idx].newValue
        constArr[idx].key = constArr[idx].newKey

        const updated = [...constArr]
        setConstArr(updated)
        propagateChangesStateUp(updated)
    }

    const handleConstAdd = () => {
        const updated = [...constArr, { key: "new_key_value", value: "some_value", editMode: false} as IConstRow]
        setConstArr(updated);
        propagateChangesStateUp(updated)
    }

    const handleConstDelete = (row: IConstRow) => {
        const filtered = [...constArr.filter(item => item.key !== row.key)]
        setConstArr(filtered);
        propagateChangesStateUp(filtered)
    }

    const handleConstValueChange = (e: any, row: IConstRow) => {
        const idx = constArr.indexOf(row)
        constArr[idx].newValue = e.target.value
        setConstArr([...constArr])
    }

    const handleConstKeyChange = (e: any, row: IConstRow) => {
        const idx = constArr.indexOf(row)
        constArr[idx].newKey = e.target.value
        setConstArr([...constArr])
    }

    const headerTemplate = (
        <div className="flex justify-content-between">
            <h4>Constants</h4>
            <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleConstAdd()}/>
        </div>
    );

    const keyBodyTemplate = (row: IConstRow) => {
        return <>
            { !row.editMode &&
                <div>
                    {row.key}
                </div>
            }

            { row.editMode &&
                <InputText placeholder="Key" value={row.newKey || ''}  onChange={ e => handleConstKeyChange(e, row) }/>
            }
        </>
    }

    const valueBodyTemplate = (row: IConstRow) => {
        return <>
            { !row.editMode &&
                <div>
                    {row.value}
                </div>
            }

            { row.editMode &&
                 <InputText placeholder="Value" value={row.newValue || ''} onChange={ e => handleConstValueChange(e, row) }/>
            }
        </>
    }


    const actionBodyTemplate = (row: IConstRow) => {
        return <>
            { !row.editMode &&
                <Button icon="pi pi-pencil" className="p-button-info p-button-rounded p-button-text" onClick={(e) => handleConstEdit(row)} />
            }

            { row.editMode &&
                <Button icon="pi pi-check" className="p-button-info p-button-rounded p-button-text" onClick={(e) => handleConstEditConfirm(row)} />
            }

            { row.editMode &&
                <Button icon="pi pi-times" className="p-button-plain p-button-rounded p-button-text" onClick={(e) => handleConstEditCancel(row)} />
            }

            <Button icon="pi pi-trash" className="const-del-btn  p-button-rounded p-button-text p-button-danger" onClick={(e) => confirmWrapper(e, () => handleConstDelete(row))} />
        </>
    }


    return <>
        <DataTable value={constArr}
                   responsiveLayout="scroll"
                   header={headerTemplate}
                   width={"100%"}
                   size="small"
        >
            <Column header="Key" body={keyBodyTemplate} style={{width: "20rem"}}/>
            <Column header="Value" body={valueBodyTemplate}/>
            <Column body={actionBodyTemplate} bodyStyle={{textAlign: "right"}}/>
        </DataTable>

        <ConfirmPopup />
    </>
}

function mapToList(map: Record<string, string>): IConstRow[] {
    return Object.keys(map).map((k) => {
        return { key: k, value: map[k], editMode: false} as IConstRow;
    })
}